import React, { useContext, useEffect, useState } from "react"
import { Combobox, Disclosure } from "@headlessui/react"
import { Form, Grid, Popup, Select, Transition } from "semantic-ui-react"

import "react-dates/initialize"
import { isInclusivelyBeforeDay, SingleDatePicker } from "react-dates"
import "react-dates/lib/css/_datepicker.css"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import moment, { Moment } from "moment"
import { useCookies } from "react-cookie"
import { useDebouncedCallback } from "use-debounce"
import api from "../../../../api2"
import { CAMPING_STYLES, RESERVATION_KINDS } from "~/constants"
import RECURRING_LIMIT_IN_YEARS from "../../../../constants/recurring_limit_in_years"
import { RIG_TYPES } from "~/constants/rigTypes"
import { AdminContext } from "~/contexts/AdminContext"
import { NewReservationContext } from "~/contexts/admin/NewReservationContext"
import { CampingStyleProps } from "../../../../interfaces"
import ICamper from "../../../../interfaces/ICamper"
import { validElectric, validRigType } from "~/types"
import { formatPhoneNumber, titleCase } from "../../../../utils"
import electricOptionsFor from "../../../../utils/admin/electricOptionsFor"
import { formatName } from "~/utils/formatName"
import SitesDropdown from "../../../Reservations/SitesDropdown/SitesDropdown"
import ReservationTypeSelector from "../../../ui/ReservationTypeSelector"
import RichTextInput from "../../../ui/RichTextInput"
import SelectInput, { Option } from "../../../ui/SelectInput"
import Spinner from "../../../ui/Spinner"
import { CamperBlocked } from "../../../ui/StatusBadges"
import slugify from "~/utils/admin/slugify"
import BookingIntervalsDropdown from "../BookingIntervalsDropdown"
import { NewReservationAction } from "~/reducers/newReservation"
import parseDateTime, { toMoment } from "~/utils/parseDateTime"
import HoverTip from "~/components/ui/HoverTip"
import ProfileCard from "~/components/Admin/Campers/Camper/ProfileCard"
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid"
import useExistingCamperWillBeOverridden from "~/components/Admin/Reservations/ReservationHooks/useExistingCamperWillBeOverridden"
import { bySort } from "~/utils/sorts"
import { isMobile } from "react-device-detect"
import PhoneInputField from "~/components/ui/Admin/PhoneInputField"
import handlePhoneNumberChange from "../../../../utils/admin/handlePhoneNumberChange"

interface ComponentProps {
  isProcessing: boolean
  errorResponse: any
  rigLengthError: string | null
  setRigLengthError: React.Dispatch<React.SetStateAction<string | null>>
}

const CreateReservationInfo = (props: ComponentProps) => {
  const { camp, campingStyles, sites, selectedCamper } = useContext(AdminContext)

  const [inputFocusedStartDate, setInputFocusedStartDate] = useState(false)
  const [inputFocusedEndDate, setInputFocusedEndDate] = useState(false)
  const [cookies, setCookie] = useCookies(["isAdditionalInfoShown", "isTaxAdded"])
  const [isShown, setIsShown] = useState(cookies.isAdditionalInfoShown === "true")
  const [tempEndDate, setTempEndDate] = useState<Moment | null>(null)
  const { existingCamperToBeOverridden } = useExistingCamperWillBeOverridden()
  const {
    newReservation,
    dispatch,
    inputNotes,
    setInputNotes,
    inputFirstName,
    setInputFirstName,
    inputLastName,
    setInputLastName,
    inputEmail,
    setInputEmail,
    inputPhone,
    setInputPhone,
    inputPhoneNumber,
    setInputPhoneNumber,
    inputPhoneCountryCode,
    setInputPhoneCountryCode,
    inputVehicleRigLength,
    setInputVehicleRigLength,
    inputVehicleElectric,
    setInputVehicleElectric,
    inputVehicleRigType,
    setInputVehicleRigType,
    inputVehicleLicense,
    setInputVehicleLicense,
    inputVehicleYear,
    setInputVehicleYear,
    inputVehicleMake,
    setInputVehicleMake,
    inputVehicleColor,
    setInputVehicleColor,
    inputAdults,
    setInputAdults,
    inputChildren,
    setInputChildren,
    inputPets,
    setInputPets,
    address,
    setAddress,
    selectedReservationType,
    setSelectedReservationType,
    selectedSitesForGroup,
    setSelectedSitesForGroup,
    setLineItems,
    isLoadingOrderPreview,
  } = useContext(NewReservationContext)

  const selectedSite = sites?.find((site) => site.id === newReservation.site_id)
  let campingStyle: CampingStyleProps | undefined

  if (selectedSite) {
    campingStyle = campingStyles.find((cs) => cs.id === selectedSite.camping_style_id)
  }

  const bookingIntervals = campingStyle?.booking_intervals
  const hasBookingIntervals = Number(bookingIntervals?.length) > 0
  const defaultHours = Number(campingStyle?.default_hours)
  const isHourly = hasBookingIntervals || defaultHours > 0

  const [startTime, setStartTime] = useState(
    campingStyle?.booking_intervals?.sort(bySort)?.find((interval) => interval.id === newReservation.interval_id)
      ?.start_time ||
    newReservation.start_date?.format("HH:mm") ||
    moment().hours(9).minutes(0).format("HH:mm"),
  )
  const [endTime, setEndTime] = useState(
    campingStyle?.booking_intervals?.sort(bySort).find((interval) => interval.id === newReservation.interval_id)
      ?.end_time ||
    newReservation.end_date?.format("HH:mm") ||
    moment().hours(9).minutes(0).add(defaultHours, "hours").format("HH:mm"),
  )

  const handleDisclosureClick = () => {
    setCookie("isAdditionalInfoShown", !isShown)
    setIsShown(!isShown)
  }

  useEffect(() => {
    if (
      newReservation.start_date &&
      newReservation.end_date &&
      newReservation.end_date.diff(newReservation.start_date, "days") < 1 &&
      campingStyle?.name !== CAMPING_STYLES.BOAT_RENTAL &&
      !campingStyle?.booking_intervals?.length
    ) {
      dispatch({
        type: "UPDATE_END_DATE",
        payload: {
          end_date: moment(newReservation.start_date).add(1, "day"),
        },
      })
    }
  }, [campingStyle])

  const handleEventTypeChange = (selectedType) => {
    setSelectedReservationType(selectedType)
    setLineItems((prev) => {
      return prev?.filter((item) => item !== undefined && !item.is_always_applied) || []
    })
    dispatch({
      type: "UPDATE_STATUS_AND_KIND",
      payload: {
        status: selectedType == RESERVATION_KINDS.BLOCKED ? "blocked" : "unpaid",
        kind: selectedType == "recurring" || selectedType == "group" ? selectedType : null,
      },
    })

    if (selectedType === RESERVATION_KINDS.RECURRING) {
      if (
        newReservation.start_date &&
        newReservation.end_date &&
        newReservation.end_date.diff(newReservation.start_date, "days") < 30
      ) {
        setTempEndDate(newReservation.end_date)
        dispatch({
          type: "UPDATE_END_DATE",
          payload: {
            end_date: moment(newReservation.start_date).add(1, "month"),
          },
        })
      }
    } else {
      if (tempEndDate) {
        dispatch({
          type: "UPDATE_END_DATE",
          payload: {
            end_date: tempEndDate,
          },
        })
      }
    }
  }

  const onAutofill = (camper: ICamper) => {
    setInputFirstName(camper.first_name)
    setInputLastName(camper.last_name)
    setInputEmail(camper.email)
    setInputPhone(formatPhoneNumber(camper.phone))
    setInputPhoneNumber(camper.phone_number)
    setInputPhoneCountryCode(camper.phone_country_code)
    setInputAdults(camper.default_adults_count)
    setInputChildren(camper.default_kids_count)
    setInputPets(camper.default_pets_info)
    setInputVehicleColor(camper.vehicle_color)
    setInputVehicleLicense(camper.license_plate)
    setInputVehicleMake(camper.vehicle_make)
    setInputVehicleRigLength(camper.vehicle_rig_length)
    setInputVehicleRigType(camper.vehicle_rig_type as validRigType)
    setInputVehicleYear([undefined, null, ""].includes(camper.vehicle_year) ? null : Number(camper.vehicle_year))
    setAddress(camper.address)
    setInputVehicleElectric(camper.vehicle_electric as validElectric)
  }

  const typeOptions = [
    { label: "Single", value: "single" },
    { label: "Recurring", value: "recurring" },
    { label: "Group", value: "group" },
    { label: "Blocked", value: "blocked" },
  ]

  const electricOptions = electricOptionsFor(sites)

  const setVehicleRigLength = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rigLength = e.target.value

    if (selectedSite && Number(rigLength) > selectedSite.max_rig_length) {
      props.setRigLengthError(`The length of the rig should not exceed ${selectedSite.max_rig_length}ft.`)
    } else {
      props.setRigLengthError(null)
    }
    setInputVehicleRigLength(rigLength)
  }
  const rigTypeOptions = [null].concat(Object.values(RIG_TYPES)).map((rigType) => ({
    key: rigType,
    value: titleCase(rigType),
    label: titleCase(rigType),
  }))

  useEffect(() => {
    if (startTime && endTime) {
      const mStartDate = parseDateTime(newReservation.start_date?.toISOString(), camp.timezone)?.set({
        hour: Number(startTime.split(":")[0]),
        minute: Number(startTime.split(":")[1]),
      })
      if (mStartDate?.isValid) {
        dispatch({
          type: NewReservationAction.UPDATE_START_DATE,
          payload: { start_date: toMoment(mStartDate) },
        })
        dispatch({
          type: NewReservationAction.UPDATE_START_TIME,
          payload: { start_time: startTime },
        })
      }

      const mEndDate = parseDateTime(newReservation.end_date?.toISOString(), camp.timezone)?.set({
        hour: Number(endTime.split(":")[0]),
        minute: Number(endTime.split(":")[1]),
      })
      if (mEndDate?.isValid) {
        dispatch({
          type: NewReservationAction.UPDATE_END_DATE,
          payload: { end_date: toMoment(mEndDate) },
        })
        dispatch({
          type: NewReservationAction.UPDATE_END_TIME,
          payload: { end_time: endTime },
        })
      }
    }

    return () => props.setRigLengthError(null)
  }, [])

  const updateLocalStartTime = (timeStr: string) => {
    setStartTime(timeStr)
    const splitTime = timeStr.split(":")
    dispatch({
      type: NewReservationAction.UPDATE_START_DATE,
      payload: {
        start_date: toMoment(
          parseDateTime(newReservation.start_date.toISOString(), camp.timezone).set({
            hour: Number(splitTime[0]),
            minute: Number(splitTime[1]),
          }),
        ),
      },
    })
    dispatch({
      type: NewReservationAction.UPDATE_START_TIME,
      payload: { start_time: timeStr },
    })
  }

  const updateLocalEndTime = (timeStr: string) => {
    setEndTime(timeStr)
    const splitTime = timeStr.split(":")
    dispatch({
      type: NewReservationAction.UPDATE_END_TIME,
      payload: {
        end_date: toMoment(
          parseDateTime(newReservation.end_date.toISOString(), camp.timezone).set({
            hour: Number(splitTime[0]),
            minute: Number(splitTime[1]),
          }),
        ),
      },
    })
    dispatch({
      type: NewReservationAction.UPDATE_END_TIME,
      payload: { end_time: timeStr },
    })
  }

  useEffect(() => {
    return () => props.setRigLengthError(null)
  }, [])

  return (
    <Form loading={props.isProcessing} autocomplete="new-password">
      <input autoComplete="new-password" name="hidden" type="text" style={{ display: "none" }}></input>
      <div>
        <div className="hidden sm:block">
          <ReservationTypeSelector
            options={typeOptions}
            selectedOption={selectedReservationType}
            onChange={handleEventTypeChange}
            isLoading={isLoadingOrderPreview}
          />
        </div>
        <div className="sm:hidden w-full mt-1.5 mb-5">
          <SelectInput
            options={typeOptions}
            selected={typeOptions.find((option) => option.value === selectedReservationType)}
            setSelected={(option: Option) => handleEventTypeChange(option.value)}
          />
        </div>
      </div>
      <div className="flex flex-col md:flex-row md:mt-4 justify-between pt-1">
        <div className="w-full">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Form.Field required className="!mb-0">
              <label>Arrival</label>
              <SingleDatePicker
                id="NEW_RESERVATION_START_DATE"
                block
                date={newReservation.start_date || null}
                onDateChange={(date) => {
                  dispatch({
                  type: NewReservationAction.UPDATE_START_DATE,
                  payload: { start_date: date },
                  })

                    dispatch({
                      type: NewReservationAction.UPDATE_END_DATE,
                      payload: { end_date: null },
                    })
                }}
                noBorder
                isOutsideRange={(day: Moment) => false}
                focused={inputFocusedStartDate}
                readOnly={isMobile && true}
                onFocusChange={(e) => setInputFocusedStartDate(e.focused)}
                numberOfMonths={1}
                hideKeyboardShortcutsPanel={true}
                renderDayContents={(day) => {
                  // Only render the special indicator if it's today and not selected
                  const isToday = day.isSame(moment(), "day")
                  const isSelected = newReservation.start_date && day.isSame(newReservation.start_date, "day")

                  return (
                    <div
                      className={
                        isToday && !newReservation.start_date
                          ? "bg-blue-100 rounded-full w-full h-full flex items-center justify-center"
                          : ""
                      }
                    >
                      {day.format("D")}
                    </div>
                  )
                }}
              />
            </Form.Field>
            <Form.Field required>
              <label>Departure</label>
              <SingleDatePicker
                id="NEW_RESERVATION_END_DATE"
                block
                date={newReservation.end_date || null}
                onDateChange={(date) =>
                  dispatch({
                    type: NewReservationAction.UPDATE_END_DATE,
                    payload: { end_date: date },
                  })
                }
                focused={inputFocusedEndDate}
                readOnly={isMobile && true}
                onFocusChange={(e) => setInputFocusedEndDate(e.focused)}
                numberOfMonths={1}
                initialVisibleMonth={() => newReservation.end_date || newReservation.start_date || moment()}
                hideKeyboardShortcutsPanel={true}
                noBorder
                isOutsideRange={(day: Moment) =>
                  defaultHours
                    ? moment(day).isBefore(newReservation.start_date) ||
                    moment(day).isAfter(moment(newReservation.start_date).add(RECURRING_LIMIT_IN_YEARS, "years"))
                    : isInclusivelyBeforeDay(day, newReservation.start_date) ||
                    moment(day).isAfter(moment(newReservation.start_date).add(RECURRING_LIMIT_IN_YEARS, "years"))
                }
              />
            </Form.Field>
          </div>
          {isHourly && (
            <div
              className={`my-4 custom-500-mt-1 grid ${hasBookingIntervals ? "grid-cols-2 xsm:grid-cols-4" : "grid-cols-2"} gap-3`}
            >
              {hasBookingIntervals && (
                <BookingIntervalsDropdown
                  className="col-span-2"
                  buttonStyle={{ height: "100%" }}
                  bookingIntervals={bookingIntervals}
                  selectedIntervalId={newReservation.interval_id}
                  onSelectId={(intervalId) => {
                    const interval = bookingIntervals?.find((bi) => bi.id === intervalId)
                    dispatch({
                      type: NewReservationAction.UPDATE_INTERVAL_ID,
                      payload: { interval_id: interval?.id },
                    })
                    updateLocalStartTime(interval.start_time)
                    updateLocalEndTime(interval.end_time)
                  }}
                />
              )}
              <Form.Input
                type="time"
                step="900"
                className="col-span-1 hourly-inputs"
                value={startTime}
                defaultValue={startTime}
                onChange={(e) => {
                  updateLocalStartTime(e.target.value)
                }}
                style={{ margin: 0 }}
              />
              <Form.Input
                type="time"
                step="900"
                className="col-span-1 hourly-inputs"
                value={endTime}
                onChange={(e) => {
                  updateLocalEndTime(e.target.value)
                }}
                style={{ margin: 0 }}
              />
            </div>
          )}
          <div className="w-full mb-4">
            <SitesDropdown
              label="Site"
              selectedIds={selectedSitesForGroup.map((site) => site?.id)}
              setSelectedIds={(ids) => {
                const mSites = ids.map((id) => sites?.find((site) => site.id === id)).sort((a, b) => a.sort - b.sort)

                setSelectedSitesForGroup(mSites)
                dispatch({
                  type: "UPDATE_SITE_ID",
                  payload: { site_id: mSites[0]?.id },
                })
              }}
              reservation={newReservation}
              selectedReservationType={selectedReservationType}
              required
              fetchSitesAvailability
            />
          </div>
          {selectedReservationType === RESERVATION_KINDS.BLOCKED ? (
            <div className="mb-2">
              <Form.TextArea
                error={props.errorResponse?.errors?.notes}
                label="Blocker notes"
                value={inputNotes}
                onChange={(e) => setInputNotes(e.target.value)}
              />
            </div>
          ) : (
            <>
              <Grid>
                <Grid.Column width="16">
                  <>
                    {selectedReservationType == RESERVATION_KINDS.GROUP ? (
                      <div className="mb-3 flex items-end">
                        <span className="text-lg font-semibold">Group leader info</span>
                      </div>
                    ) : (
                      <p className="text-lg font-semibold mb-3">Customer info</p>
                    )}
                    {existingCamperToBeOverridden && (
                      <HoverTip
                        white
                        wrap
                        width="w-auto"
                        tipPosition={window.screen.width < 768 ? "fixed" : "absolute"}
                        placement="top"
                        className="my-2"
                        content={
                          <div
                            className="flex flex-col"
                            style={{ minWidth: (window.screen.width > 768 && 500) || "auto" }}
                          >
                            <span className="mb-2 text-base">
                              The current guest data will overwrite an existing guest:
                            </span>
                            <ProfileCard camper={selectedCamper || existingCamperToBeOverridden} readOnly />
                          </div>
                        }
                        contentClass="mb-2 text-left"
                      >
                        <div className="flex items-center transition-colors duration-100 rounded-lg px-2 py-0.5 text-sm bg-yellow-100 text-yellow-600 hover:bg-yellow-200 hover:text-yellow-700">
                          <ExclamationTriangleIcon className="mr-1.5 w-3 h-3" />
                          <span>Updating existing guest</span>
                        </div>
                      </HoverTip>
                    )}
                  </>
                  <Form.Group widths="equal">
                    <input type="text" autoComplete="off" className="hidden"></input>
                    <AutocompleteField
                      label="First name"
                      error={props.errorResponse?.errors?.first_name}
                      value={inputFirstName}
                      onChange={(e: { target: { value: string } }) => setInputFirstName(e.target.value)}
                      onAutofill={onAutofill}
                    />
                    <AutocompleteField
                      label="Last name"
                      error={props.errorResponse?.errors?.last_name}
                      value={inputLastName}
                      onChange={(e: { target: { value: string } }) => setInputLastName(e.target.value)}
                      onAutofill={onAutofill}
                    />
                  </Form.Group>
                  <Form.Group widths="equal">
                    <AutocompleteField
                      label="Email"
                      className="ghostinspector-new-reservation-email-input"
                      error={props.errorResponse?.errors?.email}
                      value={inputEmail}
                      onChange={(e: { target: { value: string } }) => setInputEmail(e.target.value)}
                      onAutofill={onAutofill}
                    />
                    <AutocompleteField
                      label="Phone"
                      error={props.errorResponse?.errors?.phone}
                      value={`${inputPhoneCountryCode}${inputPhoneNumber}`}
                      phoneCountryCode={inputPhoneCountryCode}
                      onChange={(phone, country) => {
                        const { phone_number, phone_country_code } = handlePhoneNumberChange(phone, country)

                        setInputPhone(phone)
                        setInputPhoneNumber(phone_number)
                        setInputPhoneCountryCode(phone_country_code)
                      }}
                      onAutofill={onAutofill}
                    />
                  </Form.Group>
                </Grid.Column>
              </Grid>
              <Form.Group widths="equal">
                <div className="p-2 w-full">
                  <label className="text-sm font-bold">Reservation notes</label>
                  <RichTextInput value={inputNotes} setValue={setInputNotes} style={{ marginTop: 4 }} />
                </div>
              </Form.Group>
              {newReservation.kind != RESERVATION_KINDS.GROUP && (
                <Disclosure defaultOpen={isShown}>
                  {({ open }) => (
                    /* Use the `open` state to conditionally change the direction of an icon. */
                    <>
                      <Disclosure.Button
                        onClick={handleDisclosureClick}
                        className="flex justify-start w-full mt-4 mb-3"
                      >
                        <ChevronRightIcon
                          className={`${open ? "rotate-90 transform" : ""} h-5.5 w-5.5 text-gray-700 mr-2 mt-0.5`}
                        />
                        <div className="text-lg font-semibold">Additional info</div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="w-full">
                        <Form.Group widths="equal" className="relative">
                          <Popup
                            trigger={
                              <Form.Input
                                className="flex flex-col"
                                error={props.errorResponse?.errors?.vehicle_rig_length}
                                label="Rig length"
                                value={inputVehicleRigLength}
                                onChange={setVehicleRigLength}
                              />
                            }
                            hideOnScroll
                            position="top left"
                            open={!!props?.rigLengthError?.length}
                            style={{
                              color: "rgba(239, 68, 68)",
                              fontSize: ".875rem",
                            }}
                          >
                            {props?.rigLengthError}
                          </Popup>
                          <div className="w-full flex-col items-start mb-3.5 md:mb-0 px-2 md:px-0">
                            <div className=" text-sm font-bold mb-1.5">Amperage</div>
                            <SelectInput
                              options={electricOptions}
                              selected={
                                electricOptions.find((o) => o.value === inputVehicleElectric) ||
                                electricOptions.find((o) => o.value === null || "\u00A0")
                              }
                              setSelected={(option: Option) => setInputVehicleElectric(option.value as validElectric)}
                              buttonSize="normal"
                            />
                          </div>
                          <div className="w-full flex-col items-start md:ml-4 px-2 md:px-0">
                            <div className="text-sm font-bold mb-1.5">RV Type</div>
                            <SelectInput
                              options={rigTypeOptions}
                              selected={rigTypeOptions.find((o) => o.value === inputVehicleRigType)}
                              setSelected={(option: Option) => setInputVehicleRigType(option.value as validRigType)}
                              buttonSize="normal"
                            />
                          </div>
                        </Form.Group>
                        <Form.Group widths="equal">
                          <Form.Input
                            fluid
                            error={props.errorResponse?.errors?.license_plate}
                            label="License plate"
                            value={inputVehicleLicense}
                            onChange={(e) => setInputVehicleLicense(e.target.value)}
                          />
                          <Form.Field>
                            <label>Year</label>
                            <Select
                              fluid
                              error={props.errorResponse?.errors?.vehicle_year}
                              className="sites-filter-dropdown"
                              options={Array.from(
                                new Array(new Date().getFullYear() - 1948),
                                (x, i) => new Date().getFullYear() - i + 1,
                              ).map((x) => {
                                return { key: x, value: x, text: x }
                              })}
                              value={inputVehicleYear}
                              onChange={(e, { value }) => setInputVehicleYear(value as number)}
                            />
                          </Form.Field>
                          <Form.Input
                            fluid
                            error={props.errorResponse?.errors?.vehicle_make}
                            label="Make"
                            value={inputVehicleMake}
                            onChange={(e) => setInputVehicleMake(e.target.value)}
                          />
                          <Form.Input
                            fluid
                            error={props.errorResponse?.errors?.vehicle_color}
                            label="Color"
                            value={inputVehicleColor}
                            onChange={(e) => setInputVehicleColor(e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group widths="equal">
                          <Form.Input
                            type="number"
                            error={props.errorResponse?.errors?.passengers}
                            label="Adults"
                            value={inputAdults}
                            onChange={(e) => setInputAdults(parseInt(e.target.value, 10))}
                          />
                          <Form.Input
                            type="number"
                            error={props.errorResponse?.errors?.kid_campers}
                            label="Kids"
                            value={inputChildren}
                            onChange={(e) => setInputChildren(parseInt(e.target.value, 10))}
                          />
                          <Form.Input
                            type={Number(campingStyle?.extra_pets_price_in_cents) > 0 ? "number" : "string"}
                            error={props.errorResponse?.errors?.pets_info}
                            label="Pets"
                            value={inputPets}
                            onChange={(e) =>
                              setInputPets(
                                Number(campingStyle?.extra_pets_price_in_cents) > 0
                                  ? parseInt(e.target.value, 10)
                                  : e.target.value,
                              )
                            }
                          />
                        </Form.Group>
                        <Form.Group widths="equal">
                          <Form.TextArea
                            type="string"
                            error={props.errorResponse?.errors?.address}
                            label="Address"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                          />
                        </Form.Group>
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              )}
            </>
          )}
        </div>
      </div>
    </Form>
  )
}

const AutocompleteField = ({
  ref,
  label,
  className,
  error,
  value,
  onChange,
  onAutofill,
}: {
  ref?: React.Ref<HTMLInputElement>
  label: string
  className?: string
  error: string
  value: string
  onChange: any
  onAutofill: any
}) => {
  const { camp } = useContext(AdminContext)
  const [isLoading, setIsLoading] = useState(false)
  const [campers, setCampers] = useState<ICamper[]>([])
  const [query, setQuery] = useState(value || '')
  const [isOpen, setIsOpen] = useState(false)
  const uniqueId = React.useMemo(() => `field-${Math.random().toString(36).substring(2, 11)}`, [])
  const containerRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    setQuery(value || '')
  }, [value])

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchend', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchend', handleClickOutside)
    }
  }, [])

  const reloadSearchResults = useDebouncedCallback(async (query: string) => {
    if (!query || query.trim().length === 0) {
      setCampers([])
      setIsLoading(false)
      return
    }

    api.Search.campers(camp.id, query)
      .then(({ campers }) => {
        setCampers(campers)
        setIsLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setIsLoading(false)
      })
  }, 1000)

  const handleChange = (newValue: string, country?: any) => {
    setQuery(newValue)

    if (label === "Phone") {
      onChange(newValue, country)
    } else {
      onChange({ target: { value: newValue } })
    }

    if (newValue && newValue.trim().length > 0) {
      setIsLoading(true)
      setIsOpen(true)
      reloadSearchResults(newValue)
    } else {
      setIsOpen(false)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()

      if (campers.length > 0 && isOpen) {
        const firstValidCamper = campers.find(c => c.first_name || c.last_name || c.email || c.phone)
        if (firstValidCamper) {
          onAutofill(firstValidCamper)
          setIsOpen(false)
        }
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false)
    } else if (event.key === 'Tab') {
      setIsOpen(false)
    }
  }

  const handleSelect = (camper: ICamper) => {
    onAutofill(camper)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="w-full flex flex-col ml-2 mr-2 relative">
      <div className="mb-1 text-sm font-bold">{label}</div>
      <div className="relative">
        {label === "Phone" ? (
          <PhoneInputField
            inputClass="input-phone-number-reservation"
            inputStyle={{
              paddingLeft: 42,
              paddingTop: 17,
              paddingBottom: 18,
              width: "100%",
              display: "block",
              borderRadius: ".28571429rem",
              border: "1px solid rgba(34, 36, 38, .15)",
              backgroundColor: "#ffffff"
            }}
            value={value}
            camp={camp}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              // Delay closing to allow for click events to process first
              setTimeout(() => setIsOpen(false), 200)
            }}
          />
        ) : (
          <input
            ref={ref}
            data-testid={`guest-search-${slugify(label)}-input`}
            className={className}
            autoComplete="new-password"
            name={uniqueId}
            value={query}
            onChange={(event) => handleChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              // Delay closing to allow for click events to process first
              setTimeout(() => setIsOpen(false), 200)
            }}
          />
        )}
      </div>

      {isOpen && (
        <div className="z-50 shadow-2xl rounded-md bg-white w-full py-2 absolute mt-20">
          <div className="text-sm font-bold px-3 py-2">
            Previous campers {!isLoading ? ` (${campers.length} results)` : ""}
          </div>
          {isLoading ? (
            <div className="w-full h-32 flex flex-col items-center justify-center">
              <span className="text-sm">Loading...</span>
              <Spinner size={5} />
            </div>
          ) : (
            <div className="flex flex-col max-h-64 overflow-y-scroll overflow-x-hidden">
              {campers
                .filter((c) => c.first_name || c.last_name || c.email || c.phone)
                .map((camper, index) => (
                  <div
                    key={camper.id}
                    data-testid={`guest-search-${slugify(label)}-option-${index}`}
                    onClick={() => handleSelect(camper)}
                    className="cursor-pointer hover:bg-gray-100 py-1.5 px-3"
                  >
                    <div className="flex">
                      {formatName(camper as any)} {camper.blocked_at && <CamperBlocked small />}
                    </div>
                    <div className={camper.blocked_at ? "text-gray-400" : "text-black"}>
                      {camper.email ||
                        (camper.phone_country_code && camper.phone_number ? `${formatPhoneNumber(camper.phone_country_code + camper.phone_number)}` : "")}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CreateReservationInfo
